/** Pure helpers of the recurrence editor (unit tested). */
import { isClock, isIsoDate } from '@/shared/lib/calendar';

import type { ExceptionPolicy, RecurrenceDraft, RecurrenceRule, TemplateEdit } from './types';

export type RecurrenceError =
  | 'startRequired'
  | 'startInvalid'
  | 'untilRequired'
  | 'untilInvalid'
  | 'untilBeforeStart'
  | 'weekdays'
  | 'interval'
  | 'time';

export const MAX_INTERVAL = 365;

/** A sensible starting point: weekly on the weekday of `startDate`, no end. */
export function defaultDraft(startDate: string): RecurrenceDraft {
  const weekday = isIsoDate(startDate)
    ? (new Date(`${startDate}T00:00:00Z`).getUTCDay() + 6) % 7 // 0 = Monday
    : 0;
  return {
    frequency: 'weekly',
    interval: 1,
    weekdays: [weekday],
    startDate,
    timeOfDay: '',
    endMode: 'never',
    untilDate: '',
  };
}

export function draftFromRule(rule: RecurrenceRule): RecurrenceDraft {
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    weekdays: [...rule.weekdays],
    startDate: rule.start_date,
    timeOfDay: rule.time_of_day === '00:00:00' ? '' : rule.time_of_day.slice(0, 5),
    endMode: rule.until_date ? 'date' : 'never',
    untilDate: rule.until_date ?? '',
  };
}

export function validateRecurrence(draft: RecurrenceDraft): RecurrenceError[] {
  const errors: RecurrenceError[] = [];
  if (!draft.startDate) errors.push('startRequired');
  else if (!isIsoDate(draft.startDate)) errors.push('startInvalid');
  if (draft.endMode === 'date') {
    if (!draft.untilDate) errors.push('untilRequired');
    else if (!isIsoDate(draft.untilDate)) errors.push('untilInvalid');
    else if (draft.startDate && draft.untilDate < draft.startDate) errors.push('untilBeforeStart');
  }
  if (draft.frequency === 'weekly' && draft.weekdays.length === 0) errors.push('weekdays');
  if (!Number.isInteger(draft.interval) || draft.interval < 1 || draft.interval > MAX_INTERVAL) {
    errors.push('interval');
  }
  if (draft.timeOfDay && !isClock(draft.timeOfDay)) errors.push('time');
  return errors;
}

/** The request body (`PUT /cards/{id}/recurrence` or the `recurrence` of a new card). */
export function draftToInput(draft: RecurrenceDraft, timezone: string | null) {
  return {
    frequency: draft.frequency,
    interval: draft.interval,
    weekdays: draft.frequency === 'weekly' ? [...draft.weekdays].sort((a, b) => a - b) : [],
    start_date: draft.startDate,
    time_of_day: draft.timeOfDay || null,
    until_date: draft.endMode === 'date' ? draft.untilDate : null,
    ...(timezone && { timezone }),
  };
}

export function toggleWeekday(weekdays: readonly number[], day: number): number[] {
  return weekdays.includes(day)
    ? weekdays.filter((d) => d !== day)
    : [...weekdays, day].sort((a, b) => a - b);
}

/** The request body of a series save, with the optional template edits and exception policy. */
export function saveBody(
  draft: RecurrenceDraft,
  timezone: string | null,
  extra: {
    expectedVersion?: number;
    template?: TemplateEdit;
    exceptions?: ExceptionPolicy;
    resetDates?: readonly string[];
    refreshFromSource?: boolean;
  } = {},
) {
  return {
    ...draftToInput(draft, timezone),
    ...(extra.expectedVersion !== undefined && { expected_version: extra.expectedVersion }),
    ...(extra.template && { template: extra.template }),
    ...(extra.exceptions && { exceptions: extra.exceptions }),
    ...(extra.resetDates && extra.resetDates.length > 0 && { reset_dates: [...extra.resetDates] }),
    ...(extra.refreshFromSource && { refresh_from_source: true }),
  };
}

export type SaveExtra = Parameters<typeof saveBody>[2];

/** Turns an empty/zero value of a template field into "unchanged". */
export function templateEdit(rule: RecurrenceRule, edit: TemplateEdit): TemplateEdit {
  const result: TemplateEdit = {};
  if (edit.title !== undefined && edit.title.trim() && edit.title !== rule.title) {
    result.title = edit.title.trim();
  }
  if (edit.start_time && edit.start_time !== rule.start_time?.slice(0, 5)) {
    result.start_time = edit.start_time;
  }
  if (edit.end_time && edit.end_time !== rule.end_time?.slice(0, 5)) {
    result.end_time = edit.end_time;
  }
  if (edit.length_days !== undefined && edit.length_days !== rule.length_days) {
    result.length_days = edit.length_days;
  }
  if (edit.overdue_enabled !== undefined && edit.overdue_enabled !== rule.overdue_enabled) {
    result.overdue_enabled = edit.overdue_enabled;
  }
  return result;
}
